import {Subscription} from "rxjs";
import {NgClass} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {PublicAdDetailResponse} from "@numizmat/services-models/core";

import {NgbModalRef} from "@ng-bootstrap/ng-bootstrap";

import {LoginModalComponent} from "@numizmat/modals";
import {AdStatus, AdType} from "@numizmat/enumerations";
import {CurrentUser} from "@numizmat/services-models/account";
import {CurrentUserStateService} from "@numizmat/services/account";
import {AlertService, ModalService} from "@numizmat/services/common";
import {AdDetailStateService} from "@numizmat/services/core/ad-detail/ad-detail-state.service";
import {
  BidConfirmModalComponent
} from "@numizmat/pages/ad-detail-page/modals/bid-confirm-modal/bid-confirm-modal.component";
import {
  BidHistoryModalComponent
} from "@numizmat/pages/ad-detail-page/modals/bid-history-modal/bid-history-modal.component";
import {
  PurchaseConfirmModalComponent
} from "@numizmat/pages/ad-detail-page/modals/purchase-confirm-modal/purchase-confirm-modal.component";
import {AdOfferModalComponent} from "@numizmat/pages/ad-detail-page/modals/ad-offer-modal/ad-offer-modal.component";

@Component({
    selector: 'nz-ad-pricing-panel',
    imports: [
        NgClass,
        FormsModule
    ],
    templateUrl: './ad-pricing-panel.component.html',
    styleUrl: './ad-pricing-panel.component.scss'
})
export class AdPricingPanelComponent implements OnInit, OnDestroy {
  private modalService: ModalService = inject(ModalService);
  private alertService: AlertService = inject(AlertService);
  private adDetailStateService: AdDetailStateService = inject(AdDetailStateService);
  private currentUserStateService: CurrentUserStateService = inject(CurrentUserStateService);

  private loggedIn: boolean = false;
  private subs: Subscription = new Subscription();

  protected bidAmount: number = 0;
  protected minBidAmount: number = 0;
  protected adDetails!: PublicAdDetailResponse;

  protected readonly AdType = AdType;
  protected readonly AdStatus = AdStatus;

  public ngOnInit(): void {
    this.getCurrentUser();
    this.getPublicAdDetails();
  }

  public ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  protected onOpenPurchaseConfirmModal() {
    if (this.loggedIn) {
      this.modalService.open(PurchaseConfirmModalComponent, {
        scrollable: true
      });
    } else {
      this.modalService.open(LoginModalComponent, {
        scrollable: true
      });
    }
  }

  protected onOpenBidConfirmModal(): void {
    if (this.loggedIn) {
      const modalRef: NgbModalRef = this.modalService.open(BidConfirmModalComponent, {
        scrollable: true
      });
      modalRef.componentInstance.bidAmount = Number(this.bidAmount.toFixed(2));
    } else {
      this.modalService.open(LoginModalComponent, {
        scrollable: true
      });
    }
  }

  protected onOpenBidHistoryModal(): void {
    const modalRef: NgbModalRef = this.modalService.open(BidHistoryModalComponent, {
      scrollable: true
    });
    modalRef.componentInstance.adId = this.adDetails.adId;
  }

  protected onOpenAdOfferModal(): void {
    if (this.loggedIn) {
      const modalRef: NgbModalRef = this.modalService.open(AdOfferModalComponent, {
        scrollable: true
      });
      modalRef.componentInstance.adId = this.adDetails.adId;
      modalRef.componentInstance.adTitle = this.adDetails.title;
      modalRef.componentInstance.adPrice = this.adDetails.price;
    } else {
      this.modalService.open(LoginModalComponent, {
        scrollable: true
      });
    }
  }

  protected increaseBidAmount(): void {
    this.bidAmount = Number((this.bidAmount + this.adDetails.bidIncrement).toFixed(2));
  }

  protected decreaseBidAmount(): void {
    const currentBidAmount = Number((this.bidAmount - this.adDetails.bidIncrement).toFixed(2));
    if (currentBidAmount < this.minBidAmount) {
      this.bidAmount = Number(this.minBidAmount.toFixed(2));
    } else {
      this.bidAmount = Number(currentBidAmount.toFixed(2));
    }
  }

  protected showAdTypeInfo() {
    if (this.adDetails.adType === this.AdType.DirectSale) {
      this.alertService.showPublicDirectSaleInfo()
    } else {
      this.alertService.showPublicAuctionInfo()
    }
  }

  private getCurrentUser() {
    const subscription: Subscription = this.currentUserStateService.getCurrentUser().subscribe({
      next: (currentUser: CurrentUser | undefined) => {
        if (!!currentUser) {
          this.loggedIn = currentUser.loggedIn;
        }
      }
    });
    this.subs.add(subscription);
  }

  private getPublicAdDetails(): void {
    const subscription: Subscription = this.adDetailStateService.getPublicAdDetails().subscribe({
      next: (adDetails: PublicAdDetailResponse | undefined) => {
        if (!!adDetails) {
          this.adDetails = adDetails;
          this.minBidAmount = Number((this.adDetails.bidCount === 0
            ? this.adDetails.price
            : this.adDetails.price + this.adDetails.bidIncrement).toFixed(2));
          this.bidAmount = Number(this.minBidAmount.toFixed(2));
        }
      }
    });
    this.subs.add(subscription);
  }
}
